/**
 * Single source of truth for the Hyperliquid environment (testnet | mainnet).
 *
 * ALL Hyperliquid code must read its URLs and signing `source` from here (via `useEnv()` in React, or
 * `getEnvConfig(name)` outside React). Never hard-code api.hyperliquid*.xyz anywhere else.
 * The data itself lives in `config/networks.ts` (React-free, shared with `backend/`).
 */
import { createContext, createElement, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { DEFAULT_ENV, ENV_STORAGE_KEY, getEnvConfig, isEnvName, type EnvConfig, type EnvName } from './networks';

export { DEFAULT_ENV, ENVIRONMENTS, ENV_STORAGE_KEY, getEnvConfig, isEnvName } from './networks';
export type { EnvConfig, EnvName } from './networks';

/** Reads the persisted selection; falls back to testnet on missing/invalid value or storage errors. */
export function loadEnvName(storage: Pick<Storage, 'getItem'> | undefined = safeLocalStorage()): EnvName {
  try {
    const v = storage?.getItem(ENV_STORAGE_KEY);
    return isEnvName(v) ? v : DEFAULT_ENV;
  } catch {
    return DEFAULT_ENV;
  }
}

/** Persists the selection; storage errors (private mode, quota, blocked) are ignored. */
export function saveEnvName(name: EnvName, storage: Pick<Storage, 'setItem'> | undefined = safeLocalStorage()): void {
  try {
    storage?.setItem(ENV_STORAGE_KEY, name);
  } catch {
    /* ignore */
  }
}

function safeLocalStorage(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined;
  }
}

export interface EnvContextValue {
  env: EnvConfig;
  setEnv: (name: EnvName) => void;
}

const EnvContext = createContext<EnvContextValue | null>(null);

export function EnvProvider({ children, initial }: { children: ReactNode; initial?: EnvName }) {
  const [name, setName] = useState<EnvName>(() => initial ?? loadEnvName());
  const setEnv = useCallback((next: EnvName) => {
    saveEnvName(next);
    setName(next);
  }, []);
  const value = useMemo<EnvContextValue>(() => ({ env: getEnvConfig(name), setEnv }), [name, setEnv]);
  return createElement(EnvContext.Provider, { value }, children);
}

/** The env context, or null outside <EnvProvider> (GeoGate unit tests). Prefer useEnv(). */
export function useEnvMaybe(): EnvContextValue | null {
  return useContext(EnvContext);
}

export function useEnv(): EnvContextValue {
  const ctx = useContext(EnvContext);
  if (!ctx) throw new Error('useEnv() must be used inside <EnvProvider>');
  return ctx;
}
